import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import * as _ from "lodash-es";
import { useIntl } from "react-intl";
import SupplierContext from "../../../contexts/SupplierContext";
import ISupplierModel from "../../../models/ISupplierModel";
import SupplierService from "../../../services/SupplierService";
import { UseFormRegisterReturn } from "react-hook-form";

type Props = {
    fn: any;
    name: string;
    id?: string;
    testId?: string;
    className?: string;
    skipTranslate?: boolean;
    placeholder?: string;
    onKeyDownFn?: (e: any) => void;
    disabled?: boolean;
    value?: any;
    ref?: React.RefObject<any>;
    type?: "number" | "email" | "password" | "text" | "tel" | "radio" | "time";
    autoComplete?: boolean | string;
    isRequired?: boolean;
    minLength?: number;
    maxLength?: number;
    isSmall?: boolean;
    supplierModel?: any;
    isSupplierVendorOverride?: boolean;
    min?: number;
    max?: number;
    step?: number | "any";
    showStep?: boolean;
    tabIndex?: number;
    disableKeyUp?: boolean;
    editMode?: boolean;
    // @deprecated - Use OwCheckbox instead.
    checked?: boolean;
    returnZero?: boolean;
    title?: string;
    maskedInput?: boolean;
    mask?: string;
    maskChar?: string;
    defaultClassName?: string;
    alwaysShowMask?: boolean;
    enableDebounce?: boolean;
    /**
     * The number of milliseconds to debounce at, otherwise defaults to 700
     */
    debounceMs?: number;
    disablePrecisionCheck?: boolean;
    autoFocus?: boolean;
    onWheelFn?: (e: any) => void;
    /**
     * If true, the input will be called with the fn when the user blurs the input
     */
    onBlurValidation?: boolean;
    noOuterDiv?: boolean;
    selectOnFocus?: boolean;

    register?: UseFormRegisterReturn<any>;
};

export const OwFnInput: React.FC<Props> = (props) => {
    const [className, setClassName] = useState("");
    const [visualDebounce, setVisualDebounce] = useState(false);
    const [value, setValue] = useState(props.value);
    const [eVal, setEVal] = useState<ChangeEvent<any>>();
    const [step, setStep] = useState<number | "any">(props.step || "any");
    const [min, setMin] = useState<number>();
    const [max, setMax] = useState<number>();
    const [errorState, setErrorState] = useState(false);
    const [debounceTimeoutId, setDebounceTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [hasPendingInput, setHasPendingInput] = useState(false);

    const supplierModel = useContext(SupplierContext);

    const intl = useIntl();

    useEffect(() => {
        let _errorState = false;

        if (props.isRequired) {
            if (props.minLength) {
                if (value && value.length < props.minLength) {
                    _errorState = true;
                }
            } else {
                if (_.isNil(props.value) || !props.value.length) {
                    _errorState = true;
                }
            }
        }

        // console.log("always useEffect: ", props.value);

        setErrorState(_errorState);
    }, [props.isRequired, props.minLength, value, props.value]);

    useEffect(() => {
        if (props.showStep) {
            setMin(props.min || 1);
        } else if (props.min !== undefined) {
            setMin(props.min);
        }

        let defaultClassName = " form-control form-control-sm ";

        if (props.type === "radio") {
            defaultClassName = "";
        }

        if (props.defaultClassName) {
            defaultClassName = ` ${props.defaultClassName} `;
        }

        if (props.className) {
            defaultClassName = `${defaultClassName} ${props.className} `;
        }

        const compiledClassName = className + defaultClassName + (errorState ? "is-invalid alert-danger" : "");

        setClassName(compiledClassName);
    }, [props.showStep, props.min, props.type, props.defaultClassName, props.className, errorState]);

    useEffect(() => {
        if (!eVal) return; // Skip if no event value

        if (props.enableDebounce) {
            setVisualDebounce(true);

            const delayDebounceFn = setTimeout(() => {
                if (props.enableDebounce) {
                    props.fn(eVal);
                }

                setVisualDebounce(false);
                setDebounceTimeoutId(null);
                setHasPendingInput(false);
            }, props.debounceMs || 700);

            setDebounceTimeoutId(delayDebounceFn);

            if (!props.enableDebounce) {
                props.fn(eVal);
            }

            return () => {
                clearTimeout(delayDebounceFn);
            };
        } else {
            // no debounce — emit immediately
            props.fn(eVal);
        }
    }, [eVal]); // Only depend on eVal, not value which can cause loops

    useEffect(() => {
        // Only sync from props if user isn't actively typing (no pending debounce)
        if (!hasPendingInput) {
            setValue(props.value);
        }
    }, [props.value, hasPendingInput]);

    const onChange = (e: ChangeEvent<any>) => {
        let val = e.target.value;

        // Enforce min constraint for number inputs
        if (props.type === "number" && props.min !== undefined && val !== "") {
            const numVal = Number(val);
            if (!isNaN(numVal) && numVal < props.min) {
                val = String(props.min);
                e.target.value = val;
            }
        }

        // Only update state if the value has actually changed
        if (val !== value || props.type === "radio") {
            setValue(val);
            setEVal(e);
            if (props.enableDebounce) {
                setHasPendingInput(true);
            }
        }
    };

    let placeholder = "";

    if (props.placeholder) {
        if (props.isSupplierVendorOverride && !_.isNil(props.supplierModel.vendor_name_override)) {
            placeholder = props.placeholder.replace(/vendor/g, props.supplierModel.vendor_name_override);
            // dashedPlaceholder = dashedPlaceholder.replace(/vendor/g, this.props.supplierModel.vendor_name_override);
        } else {
            placeholder = props.placeholder;
        }

        if (!props.skipTranslate) {
            placeholder = intl.formatMessage({ id: placeholder });
        }
    }

    const inputElem = (
        <input
            {...props.register}
            id={props.id}
            className={className}
            type={props.type || "text"}
            name={props.name}
            onChange={onChange}
            value={value ?? ""}
            min={min}
            max={max}
            step={step}
            placeholder={placeholder}
            disabled={props.disabled}
            onWheel={(ev) => (props.type === "number" ? ev.currentTarget.blur() : "")}
            onBlur={props.onBlurValidation ? onChange : undefined}
            data-testid={props.testId}
            onInput={(e) => {
                if (props.type === "number" && props.min !== undefined && e.currentTarget.value !== "") {
                    const numVal = Number(e.currentTarget.value);
                    if (!isNaN(numVal) && numVal < props.min) {
                        e.currentTarget.value = String(props.min);
                        setValue(props.min);
                        // Trigger onChange to update parent
                        const syntheticEvent = { ...e, target: { ...e.target, value: String(props.min) } };
                        onChange(syntheticEvent as ChangeEvent<any>);
                    }
                }
            }}
            onKeyDown={(e) => {
                // Flush debounced value on Enter key to ensure form submission uses current value
                if (e.key === "Enter" && props.enableDebounce && eVal && debounceTimeoutId) {
                    clearTimeout(debounceTimeoutId);
                    props.fn(eVal);
                    setVisualDebounce(false);
                    setDebounceTimeoutId(null);
                }

                // Call existing onKeyDownFn if provided
                if (props.onKeyDownFn) {
                    props.onKeyDownFn(e);
                }

                // Block minus key when min is 0 or positive
                if (props.type === "number" && props.min !== undefined && props.min >= 0 && e.key === "-") {
                    e.preventDefault();
                }
            }}
            onFocus={props.selectOnFocus ? (e) => e.target.select() : undefined}
            checked={props.checked}
            autoComplete={props.autoComplete === true ? "on" : props.autoComplete === false ? "off" : props.autoComplete}
        />
    );

    if (props.noOuterDiv) {
        return inputElem;
    } else {
        return <div className={"OwFnInput"}>{inputElem}</div>;
    }
};
